import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface VirtualRange {
  endIndex: number;
  offsetTop: number;
  startIndex: number;
  totalHeight: number;
}

interface UseLeadsRegistryVirtualRowsParams<TRow> {
  overscan: number;
  rowHeight: number;
  rows: TRow[];
  viewportHeight: number;
}

const getVirtualRange = ({
  itemCount,
  overscan,
  rowHeight,
  scrollTop,
  viewportHeight,
}: {
  itemCount: number;
  overscan: number;
  rowHeight: number;
  scrollTop: number;
  viewportHeight: number;
}): VirtualRange => {
  const totalHeight = itemCount * rowHeight;
  const visibleRowsCount = Math.ceil(viewportHeight / rowHeight);
  const maxStartIndex = Math.max(0, itemCount - visibleRowsCount);
  const startIndex = Math.min(
    maxStartIndex,
    Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
  );
  const endIndex = Math.min(
    itemCount,
    startIndex + visibleRowsCount + overscan * 2
  );

  return {
    endIndex,
    offsetTop: startIndex * rowHeight,
    startIndex,
    totalHeight,
  };
};

const isSameRange = (prev: VirtualRange, next: VirtualRange) =>
  prev.startIndex === next.startIndex &&
  prev.endIndex === next.endIndex &&
  prev.offsetTop === next.offsetTop &&
  prev.totalHeight === next.totalHeight;

export const useLeadsRegistryVirtualRows = <TRow>({
  overscan,
  rowHeight,
  rows,
  viewportHeight,
}: UseLeadsRegistryVirtualRowsParams<TRow>) => {
  const scrollTopRef = useRef(0);

  const calculateRange = useCallback(
    (scrollTop: number) =>
      getVirtualRange({
        itemCount: rows.length,
        overscan,
        rowHeight,
        scrollTop,
        viewportHeight,
      }),
    [overscan, rowHeight, rows.length, viewportHeight]
  );

  const [range, setRange] = useState<VirtualRange>(() => calculateRange(0));

  useEffect(() => {
    setRange((prev) => {
      const next = calculateRange(scrollTopRef.current);

      return isSameRange(prev, next) ? prev : next;
    });
  }, [calculateRange]);

  const updateScrollTop = useCallback(
    (scrollTop: number) => {
      scrollTopRef.current = scrollTop;

      setRange((prev) => {
        const next = calculateRange(scrollTop);

        return isSameRange(prev, next) ? prev : next;
      });
    },
    [calculateRange]
  );

  const visibleRows = useMemo(
    () => rows.slice(range.startIndex, range.endIndex),
    [range.endIndex, range.startIndex, rows]
  );

  return {
    range,
    updateScrollTop,
    visibleRows,
  };
};
